import {useState, useEffect} from 'react';
import axios from 'axios';
import * as R from 'ramda';
import i18n from '@/i18n';

const isUnsafeMethod = R.includes(R.__, ['post', 'put', 'delete']);
const currentLang = () => (i18n.language === 'zh' ? 'zh-TW' : i18n.language);

const api = axios.create({
  withCredentials: true,
  baseURL: process.env.REACT_APP_API_ENDPOINT,
});

const setupCSRFToken = token => {
  api.defaults.headers['X-CSRF-Token'] = token;
};

const getToken = () =>
  api.get('/me').then(({data: {csrfToken}}) => setupCSRFToken(csrfToken));

api.interceptors.request.use(config => {
  const isAbsoluteURLRegex = /^(?:\w+:)\/\//;

  if (isAbsoluteURLRegex.test(config.url)) {
    return R.merge(config, {url: config.url});
  }
  return R.merge(config, {url: `${currentLang()}/api${config.url}`});
});

api.interceptors.response.use(
  undefined,
  R.ifElse(
    R.allPass([
      R.pathEq(['response', 'status'], 422),
      R.pathOr(true, ['config', 'retry']),
      R.pathSatisfies(isUnsafeMethod, ['config', 'method']),
    ]),
    error => {
      const {config} = error;
      config.retry = false;
      return getToken().then(() => api(config));
    },
    error => Promise.reject(error)
  )
);

const useApi = (path, initVal = {}) => {
  const [data, setData] = useState(initVal);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(path)
      .then(
        result => setData(result.data),
        errorResult => setError(errorResult)
      );
  }, []);

  return [data, error];
};

// for request not through axios use
const backendURL = (path, options = {}) => {
  const {useRei = false} = options;
  const reiPrefix =
    useRei && process.env.REACT_APP_ENABLE_REI === 'true' ? '/rei' : '';
  return `${api.defaults.baseURL}${reiPrefix}/${currentLang()}/api${path}`;
};

export {setupCSRFToken, useApi, backendURL};
export default api;
